import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LeadsHeader from "./LeadsHeader";
import LeadTable from "./LeadTable";
import {
  setChildLeads,
  setFilters,
  setLeads,
  setMode,
  setParentLead,
  setSelectedLead,
} from "../../../state/reducers/leadSlice";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { toggleLeadFormOpen } from "../../../state/reducers/applicationSlice";

const Leads = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const isLeadFormOpen = useSelector(
    (state) => state.application.isLeadFormOpen
  );
  const leads = useSelector((state) => state.leads.leads);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const leadState = location.state?.leadId;
  // Fetch all leads
  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data.results?.Leads || [];
      dispatch(setLeads(data));
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to load leads");
    }
  }, [dispatch]);

  // Fetch a single lead and open the modal
  const handleViewLeadForm = useCallback(
    async (leadId) => {
      if (!leadId) return;

      try {
        setIsLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/lead/api/${leadId}/`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const selectedlead = response.data.data.parent_lead || {};
        const parent_info = response.data.data.parent_info || {};
        const childLeads = response.data.data.child_leads || [];

        // Set all the required state for the modal
        dispatch(setSelectedLead({ ...parent_info, ...selectedlead }));
        dispatch(setParentLead(parent_info));
        dispatch(setChildLeads(childLeads));
        dispatch(setMode("view"));

        // Open the modal after all data is prepared
        dispatch(toggleLeadFormOpen());

        // Navigate to the base leads route to remove the ID from URL
        if (id) {
          navigate("/admin/leads", { replace: true });
        }
      } catch (err) {
        console.error("Error fetching lead:", err);
        toast.error("Failed to load lead details");
        // Navigate to base leads route if there's an error
        if (id) {
          navigate("/admin/leads", { replace: true });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, id, navigate]
  );

  // Fetch leads on initial render
  useEffect(() => {
    fetchLeads();
    setInitialLoadDone(true);
  }, [fetchLeads]);

  // Handle opening the modal when there's an ID in the URL
  // Only run once after initial load
  useEffect(() => {
    if (id && initialLoadDone) {
      handleViewLeadForm(id);
    }
  }, [id, handleViewLeadForm, initialLoadDone]);

  useEffect(() => {
    if (leadState) {
      handleViewLeadForm(leadState);
    }
  }, [leadState]);

  return (
    <div className="flex overflow-hidden">
      {/* Main Content */}
      <div
        className={`flex-1 bg-white transition-all duration-300 ${
          isSideBarOpen ? "ml-0 sm:ml-80" : "ml-0 sm:ml-24"
        } min-h-screen w-full`}
      >
        <div className="max-w-full overflow-x-auto">
          {/* Loading overlay */}
          {isLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ">
              <div className="bg-white p-5 rounded-lg shadow-lg">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-3 text-gray-700">Loading lead details...</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className="p-4 border-b-2 border-gray-300"
            style={{ maxWidth: isSideBarOpen ? "calc(100vw - 320px)" : "100%" }}
          >
            <LeadsHeader fetchLeads={fetchLeads} />
          </div>
          <div
            className="p-4 border-b-2 border-gray-300"
            style={{
              maxWidth: isSideBarOpen ? "calc(100vw - 320px)" : "100vw",
            }}
          >
            <LeadTable fetchLeads={fetchLeads} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leads;
